import React from 'react';
import { ButtonV2, SpacingStack } from '@withjoy/joykit';
import { LayoutShell } from '../../components/LayoutShell';
import { usePaymentMethodController } from './PaymentMethod.controller';
import { ContentHeader, CtaGroup, IntegratedGiftCard, PaymentMethodSelector } from '@apps/registry/guest/components/CheckoutDialog/components';
import { PaymentMethodSelectors } from './PaymentMethod.selectors';
import { useRoutePaths } from '@apps/registry/guest/GuestRegistry.routes';
import { useHistory, useRouterHelper } from '@shared/core';
import { LoadingExpressCheckoutElement } from '@shared/core/stripe';
import { useGuestRegistryTelemetry } from '@apps/registry/guest/GuestRegistry.telemetry';
import { DonationFundPaymentMethodEnum } from '@graphql/generated-base';
import { LoadingRegistryCheckoutSettings } from '@apps/registry/common/components/LoadingRegistryCheckoutSettings';
import { useEventInfo } from '@shared/utils/eventInfo';

export const PaymentMethod = () => {
  const {
    isCashFundType,
    options,
    handleSubmit,
    onUpdatePaymentMethod,
    currentPaymentMethod,
    translations,
    eventDisplayName,
    isLoading,
    amount,
    enableJoyCredit,
    registryItem,
    isGiftWrapEnabled,
  } = usePaymentMethodController();
  const { title, cta, description, descriptionDonation, enterCardManually } = translations;
  const routePaths = useRoutePaths();
  const router = useRouterHelper();
  const history = useHistory();
  const telemetry = useGuestRegistryTelemetry();
  const { eventInfo } = useEventInfo();
  const eventId = eventInfo?.eventId;
  // Match the express-checkout `upperContent` treatment (intent="primary") so the
  // same submit action reads identically whether or not Express Checkout loads.
  // Scoped to this caller — the shared `PrimaryCta` wrapper stays neutral.
  const checkoutFallback = (
    <ButtonV2
      {...PaymentMethodSelectors.SelectPaymentMethodButton}
      intent="primary"
      shape="rounded"
      disabled={!currentPaymentMethod}
      type="submit"
      loading={isLoading}
    >
      {cta()}
    </ButtonV2>
  );

  return (
    <LayoutShell {...PaymentMethodSelectors.ContainerElement}>
      <LayoutShell.Header mobilePlacement={isCashFundType ? 'float' : 'static'} />
      <LayoutShell.Body>
        <LayoutShell.MediaPanel>
          <IntegratedGiftCard mobileRenderBehavior={isCashFundType ? 'fullBleed' : 'card'} />
        </LayoutShell.MediaPanel>
        <LayoutShell.ContentPanel>
          <ContentHeader>
            <ContentHeader.Hed>{title()}</ContentHeader.Hed>
            <ContentHeader.Dek>{isCashFundType ? description({ host: eventDisplayName }) : descriptionDonation({ fundName: eventDisplayName })}</ContentHeader.Dek>
          </ContentHeader>
          <form onSubmit={handleSubmit}>
            <SpacingStack
              spacing={7}
              marginTop={6}
            >
              <PaymentMethodSelector
                value={currentPaymentMethod}
                options={options}
                onChange={onUpdatePaymentMethod}
              />
              <CtaGroup>
                <LoadingRegistryCheckoutSettings
                  eventId={eventId}
                  fallbackContent={checkoutFallback}
                >
                  <LoadingExpressCheckoutElement
                    onParentClick={event => {
                      telemetry.expressCheckoutClicked({ ...event, source: 'CheckoutDialog_PaymentMethod' });
                      history.push(router.buildPath(routePaths.itemCheckout.goToPath(registryItem.id, Math.round(amount * 100).toString())));
                    }}
                    showFallback={!enableJoyCredit}
                    disablePaymentEvents={isGiftWrapEnabled}
                    onConfirm={() => {}}
                    onReady={event => telemetry.expressCheckoutElementReady({ ...event, source: 'CheckoutDialog_PaymentMethod' })}
                    options={{ buttonHeight: 50 }}
                    upperContent={
                      <ButtonV2
                        {...PaymentMethodSelectors.SelectPaymentMethodButton}
                        intent="primary"
                        shape="rounded"
                        disabled={!currentPaymentMethod}
                        type="submit"
                        loading={isLoading}
                      >
                        {currentPaymentMethod === DonationFundPaymentMethodEnum.joyCredit ? enterCardManually() : cta()}
                      </ButtonV2>
                    }
                    fallbackContent={checkoutFallback}
                  />
                </LoadingRegistryCheckoutSettings>
              </CtaGroup>
            </SpacingStack>
          </form>
        </LayoutShell.ContentPanel>
      </LayoutShell.Body>
    </LayoutShell>
  );
};
